import { Head, Link, useForm, usePage } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ArrowLeft } from "lucide-react"
import { InputField, TextareaField, SelectField, NumberInputField, RadioGroupField, DateField, SteppedSliderField, SearchableSelectField } from "@/components/form-field"
import { Agent, Deal } from '../../types/serializers'

export interface ExpiryPolicyOption {
  value: 'extend' | 'expire'
  label: string
  description: string
}

export const EXPIRY_POLICY_OPTIONS: ExpiryPolicyOption[] = [
  { value: "extend", label: "Extend", description: "Seek extension approval from buyer" },
  { value: "expire", label: "Expire", description: "Allow to expire — do not extend" },
]

interface Props {
  deal: Deal
  agents: Agent[]
  [key: string]: any
}

export default function Edit() {
  const { deal, agents } = usePage<Props>().props
  const { data, setData, put, processing, errors } = useForm({
    name: deal.name || '',
    salesforce_id: deal.salesforce_id || '',
    sku_id: deal.sku_id || 0,
    agent_id: deal.agent_id || 0,
    state: deal.state || 'draft',
    expiry_policy: deal.expiry_policy || 'extend',
    event_date: deal.event_date || '',
    start_date: deal.start_date || '',
    initial_term: deal.initial_term || 12,
    renewals: deal.renewals || '',
    renewal_term: deal.renewal_term || 12,
    renewals_taken: deal.renewals_taken || 0,
    fee_rate: deal.fee_rate || '',
    fee_unit: deal.fee_unit || 'Dollar',
    fee_cap: deal.fee_cap || '',
    reporting_basis: deal.reporting_basis || 'monthly',
    notes: deal.notes || ''
  })

  if (!deal) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const { name, salesforce_id, sku_id, ...submitData } = data
    put(`/deals/${deal.id}`, submitData as any)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target

    if (type === 'checkbox') {
      setData(name as keyof Deal, (e.target as HTMLInputElement).checked)
    } else {
      setData(name as keyof Deal, value)

      // If renewals is being updated, ensure renewals_taken doesn't exceed it
      if (name === 'renewals') {
        const newRenewals = parseInt(value) || 0
        const currentRenewalsTaken = parseInt(data.renewals_taken?.toString() || '0')
        if (currentRenewalsTaken > newRenewals) {
          setData('renewals_taken', newRenewals)
        }
      }
    }
  }

  const handleSelectChange = (name: string, value: string) => {
    setData(name as keyof Deal, value)
  }

  // Define select options

  const feeUnitOptions = [
    { value: 'dollar', label: 'Dollar' },
    { value: 'gallon', label: 'Gallon' },
    { value: 'event', label: 'Event' }
  ]

  const reportingBasisOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'otp_delivery', label: 'OTP (Delivery)' },
    { value: 'otp_po', label: 'OTP (PO)' },
    { value: 'fixed_fee', label: 'Fixed Fee' }
  ]

  return (
    <>
      <Head title={`Edit Deal: ${deal.name}`} />
      <div className="container mx-auto p-6">
        <div className="flex items-center gap-4 mb-6">
          <h1 className="text-3xl font-bold">Edit Deal</h1>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  <InputField
                    name="name"
                    label="Name"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                    readOnly={true}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      name="salesforce_id"
                      label="Salesforce ID"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                      readOnly={true}
                    />

                    <NumberInputField
                      name="sku_id"
                      label="SKU ID"
                      type="number"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                      readOnly={true}
                    />
                  </div>

                  <SearchableSelectField
                    name="agent_id"
                    label="Agent"
                    data={data}
                    errors={errors}
                    options={[
                      { value: '', label: 'Select an agent' },
                      ...agents.map(agent => ({
                        value: agent.id,
                        label: agent.name
                      }))
                    ]}
                    onValueChange={handleSelectChange}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <legend className="text-foreground text-sm leading-none font-medium pb-2">State</legend>
                    <RadioGroupField
                      name="state"
                      data={data}
                      errors={errors}
                      options={[
                        { value: "draft", label: "Draft", description: "Imported from Salesforce and waiting for team to confirm details" },
                        { value: "active", label: "Active", description: "Setup in accounting, ready for business" },
                        { value: "archived", label: "Archived", description: "Deal is no longer active" },
                      ]}
                      onChange={handleSelectChange}
                    />
                  </div>

                  <div>
                    <legend className="text-foreground text-sm leading-none font-medium pb-2">Expiry Policy</legend>
                    <RadioGroupField
                      name="expiry_policy"
                      data={data}
                      errors={errors}
                      options={EXPIRY_POLICY_OPTIONS}
                      onChange={handleSelectChange}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>Dates & Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <div className="grid grid-cols-3 gap-4">
                    <DateField
                      name="event_date"
                      label="Event Date"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <DateField
                      name="start_date"
                      label="Start Date"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                    />

                    <NumberInputField
                      name="initial_term"
                      label="Initial Term"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                      suffix="months"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <InputField
                      name="renewals"
                      label="Number of Renewals"
                      type="number"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                    />

                    <NumberInputField
                      name="renewal_term"
                      label="Renewal Term"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                      suffix="months"
                    />

                    <SteppedSliderField
                      name="renewals_taken"
                      label="Renewals Taken"
                      data={data}
                      errors={errors}
                      onChange={(e: { target: { name: string; value: number } }) => setData('renewals_taken', e.target.value)}
                      max={parseInt(data.renewals || 0)}
                      value={parseInt(data.renewals_taken || 0)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>Accounting</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  <div className="grid grid-cols-3 gap-4">
                    <NumberInputField
                      name="fee_rate"
                      label="Fee Rate"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                      prefix="$"
                      suffix="USD"
                    />
                    <SelectField
                      name="fee_unit"
                      label="Fee Unit"
                      data={data}
                      errors={errors}
                      options={feeUnitOptions}
                      onValueChange={handleSelectChange}
                    />

                    <NumberInputField
                      name="fee_cap"
                      label="Fee Cap"
                      data={data}
                      errors={errors}
                      onChange={handleInputChange}
                      prefix="$"
                      suffix="USD"
                    />

                    <SelectField
                      name="reporting_basis"
                      label="Reporting Basis"
                      data={data}
                      errors={errors}
                      options={reportingBasisOptions}
                      onValueChange={handleSelectChange}
                    />
                  </div>

                  <TextareaField
                    name="notes"
                    label="Notes"
                    data={data}
                    errors={errors}
                    onChange={handleInputChange}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end gap-4">
            <Link href={`/deals/${deal.id}`}>
              <Button type="button" variant="outline">Cancel</Button>
            </Link>
            <Button type="submit" disabled={processing}>
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}
